/**
 * The Message Log — the screen this platform is built around.
 *
 * Ported from design/preview/message-log.html: same grid, same density, same
 * columns. Subject absorbs the slack and truncates last, because it is what
 * people scan.
 *
 * Two details changed on 21 September, both from design/preview/
 * explore-log-details.html, where Arnis chose them from three options each:
 * the time column reads as a date and time with a card on hover, and the
 * filters are chips that stay quiet until set, with the recipient search in
 * the same row.
 */

import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { api } from "../api/client";
import { useAsync, useDismiss, usePreference } from "../hooks";
import { SkeletonRows } from "../components/Loading";
import { Icon, type IconName } from "../components/Sprites";
import { Status, StatusGlyph, statusLabel } from "../components/Status";
import { MessageDrawer } from "./MessageDrawer";
import { useSession } from "../session";
import { logStamp, stampDetail, stampSentence, zoneLabel, type LogZone } from "../logTime";
import type { Channel, CursorPage, Message, MessageStatus } from "../api/types";

const PAGE_SIZE = 50;

/**
 * Relative windows, resolved to an instant at request time.
 *
 * Presets rather than a date picker, because the question this screen answers
 * is almost always "what happened recently" — and a preset survives being
 * pasted into a ticket, where "since 14:02 on the 19th" stops meaning what the
 * sender meant the moment tomorrow arrives.
 */
const RANGES = ["1h", "24h", "7d", "30d", "90d"] as const;

const RANGE_LABEL: Record<string, string> = {
  "1h": "Last hour",
  "24h": "Last 24 hours",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
};

const RANGE_MS: Record<string, number> = {
  "1h": 60 * 60 * 1000,
  "24h": 24 * 60 * 60 * 1000,
  "7d": 7 * 24 * 60 * 60 * 1000,
  "30d": 30 * 24 * 60 * 60 * 1000,
  "90d": 90 * 24 * 60 * 60 * 1000,
};

/**
 * Resolves a window to the RFC 3339 instant the API wants.
 *
 * Returns "" for an unknown or absent range, which the query builder drops —
 * so a hand-edited URL degrades to no filter rather than to an empty log.
 *
 * ANCHORED TO THE MINUTE, AND THAT IS LOAD-BEARING.
 *
 * This value goes into the fetch's dependency array. Computed at full
 * millisecond precision it was different on every single render, so every fetch
 * settled state, the new state re-rendered, the re-render produced a new
 * `since`, and the dependency array had changed again — an unbounded refetch
 * loop running at roughly thirty requests a second.
 *
 * Nothing about it looked like a loop from the outside. The log rendered
 * correctly and the requests all returned 200, until the API rate limiter's
 * 600-per-minute budget ran out about twenty seconds in. From then on every
 * request in that window got a 429 — including ones from unrelated controls, so
 * whichever filter the user touched next appeared to be the broken one.
 *
 * Truncating the seconds makes the string stable within a minute, which breaks
 * the loop at the source; useMemo at the call site then keeps it stable across
 * renders entirely. Both, for the same reason Analytics does both: the memo
 * alone would still hand a new value to a remount, and the truncation alone
 * would still refetch once a minute forever.
 */
function rangeStart(range: string): string {
  const span = RANGE_MS[range];
  if (!span) return "";

  const start = new Date(Date.now() - span);
  start.setSeconds(0, 0);
  return start.toISOString();
}

/** The pipeline, then the problems, as the Status menu lists them. */
const PROGRESS: MessageStatus[] = ["queued", "sent", "delivered", "opened", "clicked"];
const PROBLEMS: MessageStatus[] = ["bounced", "complained", "failed", "unsubscribed"];

const CHANNEL_ICON: Record<Channel, IconName> = {
  email: "mail",
  sms: "sms",
  push: "push",
  whatsapp: "sms",
  webhook: "external",
};

const isZone = (value: string): value is LogZone => value === "local" || value === "utc";

export function MessageLog() {
  const { orgId, session } = useSession();
  const navigate = useNavigate();
  const { messageId } = useParams();

  // Filters live in the URL. A support conversation is mostly people sending
  // each other links to a filtered view; state held only in React cannot be
  // pasted into a ticket.
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "";
  const channel = params.get("channel") ?? "";
  const to = params.get("to") ?? "";

  // A relative window ("24h") rather than two absolute instants in the URL.
  // Absolute timestamps would make a pasted link mean something different
  // tomorrow than it did when it was copied, which is the opposite of what
  // sending a colleague a filtered view is for.
  const range = params.get("range") ?? "";

  // Memoised on `range` alone, so the value handed to the fetch below is
  // identical across renders. See rangeStart: an unmemoised, unanchored version
  // of this line was a thirty-requests-a-second refetch loop that presented as
  // an unrelated filter returning 429.
  const since = useMemo(() => rangeStart(range), [range]);

  // Set by the campaign report's outcome metrics, never by a control on this
  // screen — there is no campaign picker here, and a list of every campaign
  // an organization has ever sent would be a poor one. It still gets a chip
  // below: arriving at a narrowed log with nothing saying so is the same
  // failure as a filter that silently matches nothing.
  const campaignId = params.get("campaign_id") ?? "";

  // Which clock the Time column reads in. A viewer's preference rather than a
  // filter, so it is remembered for the person rather than put in the URL: a
  // colleague opening a pasted link should see times in their own zone.
  const [zone, setZone] = usePreference<LogZone>("log-zone", session?.user.id, "local", isZone);

  // Cursors are forward-only, so going back means remembering where each page
  // started rather than computing an offset.
  const [history, setHistory] = useState<string[]>([]);
  const cursor = history.at(-1) ?? "";

  const { data, error, loading, reload } = useAsync<CursorPage<Message>>(
    (signal) =>
      api.get<CursorPage<Message>>("/v1/messages", {
        signal,
        query: { limit: PAGE_SIZE, cursor, status, channel, to, since, campaign_id: campaignId },
      }),
    [orgId, cursor, status, channel, to, since, campaignId],
  );

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
    setHistory([]); // A new filter is a new result set; old cursors do not apply.
  };

  const clearAll = () => {
    setParams(new URLSearchParams(), { replace: true });
    setHistory([]);
  };

  const active = [status, channel, to, range, campaignId].filter(Boolean).length;

  // Only one filter menu is open at a time; opening one closes the other.
  const [menu, setMenu] = useState<"status" | "when" | null>(null);

  // The hover card for one row's time. Fixed to the viewport rather than
  // inside the row, because the table scrolls and clips.
  const [card, setCard] = useState<{ at: Date; left: number; top: number } | null>(null);
  const showCard = (at: Date, cell: HTMLElement) => {
    const box = cell.getBoundingClientRect();
    // Beside the time, and pulled back inside the window near its right edge.
    const left = Math.min(box.right + 10, window.innerWidth - 262);
    setCard({ at, left: Math.max(8, left), top: Math.max(8, box.top - 8) });
  };

  const rows = data?.data ?? [];
  const firstIndex = history.length * PAGE_SIZE + 1;

  return (
    <>
      <div className="topbar">
        <h1 className="oc-headline-lg">Message Log</h1>
      </div>

      <div className="filters filters--log">
        <RecipientSearch key={to} value={to} onApply={(value) => setFilter("to", value)} />

        <FilterChip
          name="status"
          label="Status"
          setLabel={status ? `Status is ${statusLabel(status as MessageStatus)}` : undefined}
          open={menu === "status"}
          onOpen={(open) => setMenu(open ? "status" : null)}
          onClear={() => setFilter("status", "")}
        >
          <StatusMenu
            value={status}
            onPick={(value) => {
              setFilter("status", value);
              setMenu(null);
            }}
          />
        </FilterChip>

        <FilterChip
          name="when"
          label="When"
          setLabel={range ? (RANGE_LABEL[range] ?? range) : undefined}
          open={menu === "when"}
          onOpen={(open) => setMenu(open ? "when" : null)}
          onClear={() => setFilter("range", "")}
        >
          <WhenMenu
            value={range}
            onPick={(value) => {
              setFilter("range", value);
              setMenu(null);
            }}
          />
        </FilterChip>

        {/* Channel is not in the row while every message is email, which it is
            in version 1: a filter whose only choice returns the whole log is a
            control nobody needs. A link that already carries one still shows
            it, with the way to clear it, and the chip comes back as a menu once
            a second channel delivers. */}
        {channel && (
          <SetChip label={`Channel is ${channel}`} onClear={() => setFilter("channel", "")} name="channel" />
        )}

        {campaignId && (
          // The id rather than the campaign's name: this screen never fetches
          // campaigns, and one request per arrival to render a label nobody
          // reads twice is not worth it. The chip's job is to explain why the
          // log is short and offer the way out.
          <SetChip
            label={`Campaign ${campaignId.slice(0, 8)}`}
            onClear={() => setFilter("campaign_id", "")}
            name="campaign"
          />
        )}

        {/* Only once two are set. With one, its own × is the way out, and a
            second control doing the same thing beside it is noise. */}
        {active >= 2 && (
          <button type="button" className="filters__clear" onClick={clearAll}>
            Clear
          </button>
        )}

        <span className="grow" />
        <button type="button" className="oc-btn oc-btn--ghost oc-btn--sm" onClick={reload}>
          <Icon name="refresh" size={12} />
          Refresh
        </button>
      </div>

      <div className="content">
        <div className="oc-panel oc-starfield" style={{ flex: 1, minWidth: 0 }}>
          <div className="oc-table log" style={{ flex: 1, minHeight: 0 }}>
            <div className="oc-table__head" role="row">
              <span>
                Time
                <button
                  type="button"
                  className="log__zone"
                  onClick={() => setZone(zone === "utc" ? "local" : "utc")}
                  aria-label={
                    zone === "utc"
                      ? `Times are in UTC. Show them in local time (${zoneLabel("local")})`
                      : `Times are in local time (${zoneLabel("local")}). Show them in UTC`
                  }
                  title={zone === "utc" ? `Show local time (${zoneLabel("local")})` : "Show UTC"}
                >
                  · {zoneLabel(zone)}
                  <Icon name="selector" size={12} />
                </button>
              </span>
              <span>Recipient</span>
              <span>Subject</span>
              <span className="oc-table__cell--center">Channel</span>
              <span className="oc-table__cell--right">Status</span>
            </div>

            <div className="oc-table__body">
              {error && <Failed detail={error.message} onRetry={reload} />}
              {loading && rows.length === 0 && <SkeletonRows columns={5} />}

              {!error && rows.length === 0 && !loading && <NoMessages filtered={active > 0} />}
              {rows.map((message) => (
                <Row
                  key={message.id}
                  message={message}
                  zone={zone}
                  selected={message.id === messageId}
                  onOpen={() => navigate(`/messages/${message.id}${window.location.search}`)}
                  onShowTime={showCard}
                  onHideTime={() => setCard(null)}
                />
              ))}
            </div>

            <div className="oc-table__footer">
              <span className="oc-mono-sm">{summary(rows.length, firstIndex, data?.estimated_total)}</span>
              <span style={{ display: "flex", gap: "var(--oc-space-2)" }}>
                <button
                  type="button"
                  className="oc-btn oc-btn--ghost oc-btn--sm"
                  disabled={history.length === 0}
                  onClick={() => setHistory((h) => h.slice(0, -1))}
                >
                  Prev
                </button>
                <button
                  type="button"
                  className="oc-btn oc-btn--ghost oc-btn--sm"
                  disabled={!data?.has_more || !data.next_cursor}
                  onClick={() => {
                    if (data?.next_cursor) setHistory((h) => [...h, data.next_cursor!]);
                  }}
                >
                  Next
                </button>
              </span>
            </div>
          </div>
        </div>
      </div>

      {card && <StampCard at={card.at} zone={zone} left={card.left} top={card.top} />}

      {messageId && (
        <MessageDrawer
          messageId={messageId}
          onClose={() => navigate(`/messages${window.location.search}`)}
        />
      )}
    </>
  );
}

function Row({
  message,
  zone,
  selected,
  onOpen,
  onShowTime,
  onHideTime,
}: {
  message: Message;
  zone: LogZone;
  selected: boolean;
  onOpen: () => void;
  onShowTime: (at: Date, cell: HTMLElement) => void;
  onHideTime: () => void;
}) {
  const at = new Date(message.created_at);
  const time = useRef<HTMLSpanElement>(null);

  return (
    <div
      className="oc-table__row"
      role="row"
      tabIndex={0}
      aria-selected={selected || undefined}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
      // A row reached by keyboard shows its card too, anchored to its time:
      // the card holds the milliseconds and the other zone, and hover is not
      // the only way anybody reads a log.
      onFocus={(event) => {
        if (event.target === event.currentTarget && time.current && event.currentTarget.matches(":focus-visible")) {
          onShowTime(at, time.current);
        }
      }}
      onBlur={onHideTime}
    >
      <span
        ref={time}
        className="log__time"
        onMouseEnter={(event) => onShowTime(at, event.currentTarget)}
        onMouseLeave={onHideTime}
      >
        {logStamp(at, zone)}
        {/* What the card shows, for a reader who cannot hover. */}
        <span className="oc-visually-hidden">, {stampSentence(at, zone)}</span>
      </span>
      <span className="oc-mono oc-truncate" style={{ color: "var(--oc-accent-bright)" }}>
        {message.to}
      </span>
      <span className="oc-body-sm oc-truncate">{message.subject}</span>
      <span className="oc-table__cell--center">
        <Icon name={CHANNEL_ICON[message.channel] ?? "mail"} className="channel-icon" />
      </span>
      <span className="oc-table__cell--right">
        <Status status={message.status} severity={message.severity} />
      </span>
    </div>
  );
}

/** The full date, both clocks to the millisecond, and how long ago. */
function StampCard({ at, zone, left, top }: { at: Date; zone: LogZone; left: number; top: number }) {
  const detail = stampDetail(at, zone);

  return (
    <div className="log__card" style={{ left, top }} aria-hidden="true">
      <div className="log__card-day">{detail.day}</div>
      {detail.lines.map((line) => (
        <div className="log__card-line" key={line.zone}>
          <span>{line.time}</span>
          <span>{line.zone}</span>
        </div>
      ))}
      <div className="log__card-relative">{detail.relative}</div>
    </div>
  );
}

/**
 * The recipient search, in the filter row rather than beside the title.
 *
 * Applied on Enter rather than per keystroke: each change is a query against a
 * table expected to hold millions of rows. Keyed on the applied value by the
 * caller, so clearing the filter from elsewhere empties the box too.
 */
function RecipientSearch({ value, onApply }: { value: string; onApply: (value: string) => void }) {
  const [draft, setDraft] = useState(value);

  return (
    <div className="oc-search filters__search">
      <Icon name="search" className="oc-search__icon" />
      <input
        className="oc-input"
        data-screen-search
        // Says "starts with" because that is what it does. An earlier label
        // invited a domain or a fragment, matched the address exactly, and
        // returned nothing — which reads as an empty log rather than as a
        // filter that did not match.
        placeholder="Recipient starts with…"
        aria-label="Recipient starts with"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") onApply(draft.trim());
        }}
      />
      {value && (
        <button
          type="button"
          className="filters__search-clear"
          aria-label="Clear the recipient filter"
          onClick={() => onApply("")}
        >
          <Icon name="close" size={12} />
        </button>
      )}
    </div>
  );
}

/**
 * A filter that is quiet until it is set.
 *
 * Unset, it is its name and a chevron. Set, it becomes one accent chip that
 * reads as a sentence — "Status is Bounced" — whose label reopens the menu and
 * whose × clears it. One control per filter in both states, where the old row
 * had a select and then a second chip repeating what the select already said.
 */
function FilterChip({
  name,
  label,
  setLabel,
  open,
  onOpen,
  onClear,
  children,
}: {
  name: string;
  label: string;
  /** Present when the filter is set. */
  setLabel?: string;
  open: boolean;
  onOpen: (open: boolean) => void;
  onClear: () => void;
  children: ReactNode;
}) {
  const container = useRef<HTMLSpanElement>(null);
  useDismiss(open, container, () => onOpen(false));
  const fit = useMenuFit(open, container);

  return (
    <span className={fit.end ? "filters__wrap filters__wrap--end" : "filters__wrap"} ref={container}>
      {setLabel ? (
        <span className="filter-set">
          <button
            type="button"
            className="filter-set__value"
            aria-haspopup="listbox"
            aria-expanded={open}
            onClick={() => onOpen(!open)}
          >
            {setLabel}
          </button>
          <button type="button" className="filter-set__clear" aria-label={`Clear the ${name} filter`} onClick={onClear}>
            <Icon name="close" size={12} />
          </button>
        </span>
      ) : (
        <button
          type="button"
          className="filter-chip"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => onOpen(!open)}
        >
          {label}
          <Icon name="chevron" size={12} className="chevron-down" />
        </button>
      )}
      {open && children}
    </span>
  );
}

/**
 * Keeps an open menu inside the window.
 *
 * A menu opens under its chip, from the chip's left edge. The row wraps, so a
 * chip can sit anywhere along it, and one near the right edge of a narrow
 * window opened a 224px menu past the window: measured at 550px, the whole page
 * gained a sideways scrollbar. Such a menu opens from the chip's right edge
 * instead. And one that would run past the bottom scrolls within the space
 * there is, because nothing behind the filter row scrolls to reveal the rest.
 *
 * Measured before paint, so a menu never draws in the wrong place first.
 */
function useMenuFit(open: boolean, container: { current: HTMLElement | null }): { end: boolean } {
  const [end, setEnd] = useState(false);

  useLayoutEffect(() => {
    if (!open) {
      setEnd(false);
      return;
    }
    const menu = container.current?.querySelector<HTMLElement>(".filter-menu");
    if (!menu) return;

    const margin = 8;
    const box = menu.getBoundingClientRect();
    setEnd(box.right > document.documentElement.clientWidth - margin);
    menu.style.maxHeight = box.bottom > window.innerHeight - margin
      ? `${Math.max(120, window.innerHeight - box.top - margin)}px`
      : "";
  }, [open, container]);

  return { end };
}

/** A set filter with no menu, only the way out. */
function SetChip({ label, onClear, name }: { label: string; onClear: () => void; name: string }) {
  return (
    <span className="filter-set">
      <span className="filter-set__value">{label}</span>
      <button type="button" className="filter-set__clear" aria-label={`Clear the ${name} filter`} onClick={onClear}>
        <Icon name="close" size={12} />
      </button>
    </span>
  );
}

function MenuOption({
  selected,
  onPick,
  children,
  quiet,
}: {
  selected: boolean;
  onPick: () => void;
  children: ReactNode;
  /** The "any" option, which resets rather than chooses. */
  quiet?: boolean;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      className={`oc-menu__option${quiet ? " filter-menu__any" : ""}`}
      onClick={onPick}
    >
      {children}
      <Icon name="check" size={14} className="filter-menu__check" />
    </button>
  );
}

/**
 * Status, with the glyph each one has everywhere else, and the problems below
 * a divider: the pipeline reads in order, and the four outcomes somebody opens
 * this menu to find are together at the bottom.
 */
function StatusMenu({ value, onPick }: { value: string; onPick: (value: string) => void }) {
  const option = (status: MessageStatus) => (
    <MenuOption key={status} selected={value === status} onPick={() => onPick(status)}>
      <StatusGlyph status={status} />
      {statusLabel(status)}
    </MenuOption>
  );

  return (
    <div className="oc-menu filter-menu" role="listbox" aria-label="Status">
      <MenuOption quiet selected={!value} onPick={() => onPick("")}>
        Any status
      </MenuOption>
      <div className="filter-menu__sep" role="separator" />
      {PROGRESS.map(option)}
      <div className="filter-menu__sep" role="separator" />
      {PROBLEMS.map(option)}
    </div>
  );
}

function WhenMenu({ value, onPick }: { value: string; onPick: (value: string) => void }) {
  return (
    <div className="oc-menu filter-menu" role="listbox" aria-label="When">
      <MenuOption quiet selected={!value} onPick={() => onPick("")}>
        Any time
      </MenuOption>
      {RANGES.map((range) => (
        <MenuOption key={range} selected={value === range} onPick={() => onPick(range)}>
          {RANGE_LABEL[range]}
        </MenuOption>
      ))}
    </div>
  );
}

/**
 * An empty state teaches rather than apologises: someone arriving here came to
 * send a message, so give them the way to send one.
 *
 * WHICH WAY DEPENDS ON WHO IS READING, and for a long time it did not. This is
 * the landing screen, so it is the first thing a brand-new account sees, and it
 * said "Create an API key, then:" above a curl command — to every role. A
 * marketing or support member cannot create an API key: `keys:manage` is in the
 * developer preset and not theirs, and the API Keys screen is absent from their
 * nav. So the product's opening instruction to a hosted client was a credential
 * they had no way to obtain and no screen on which to look for one.
 *
 * The command is not softened or removed — a developer integrating this product
 * wants exactly that snippet, and rewriting it for an audience that is not
 * theirs would break the one empty state that was already doing its job. It is
 * addressed to the person who can act on it. Same call as the per-contact
 * export, which sits behind `contacts:export` rather than `contacts:write` so
 * the analyst who handles subject access requests can actually see it.
 */
function NoMessages({ filtered }: { filtered: boolean }) {
  const { orgId, can } = useSession();

  if (filtered) {
    return (
      <div className="oc-empty">
        <div className="oc-empty__title">No messages match these filters</div>
        <div className="oc-empty__hint">Clear a filter to widen the search.</div>
      </div>
    );
  }

  if (!can("keys:manage")) {
    return (
      <div className="oc-empty">
        <div className="oc-empty__title">No messages yet</div>
        <div className="oc-empty__hint">
          Every message you send will appear here within a second — who it went to, whether it
          arrived, and whether it was opened.
        </div>
        {can("campaigns:write") && (
          <Link className="oc-btn oc-btn--primary oc-btn--sm" to="/campaigns">
            Create a campaign
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className="oc-empty">
      <div className="oc-empty__title">No messages yet</div>
      <div className="oc-empty__hint">
        Every send appears here within a second, with its delivery timeline and the provider&rsquo;s
        raw response. Create an API key, then:
      </div>
      <pre className="oc-code">{`curl ${window.location.origin}/v1/messages \\
  -H "Authorization: Bearer $OC_API_KEY" \\
  -H "Idempotency-Key: $(uuidgen)" \\
  -H "Content-Type: application/json" \\
  -d '{
    "to": "someone@example.com",
    "from": "Acme <hello@mail.example.com>",
    "subject": "Hello from OpenComms",
    "text": "It works."
  }'`}</pre>
      <div className="oc-mono-sm oc-text-muted">org {orgId}</div>
    </div>
  );
}

function Failed({ detail, onRetry }: { detail: string; onRetry: () => void }) {
  return (
    <div className="oc-empty">
      <div className="oc-empty__title">Could not load the log</div>
      <div className="oc-empty__hint">{detail}</div>
      <button type="button" className="oc-btn oc-btn--sm" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

/**
 * "Showing 1–50 of 1,204,591 events".
 *
 * The total is exact for small logs and a planner estimate for large ones, and
 * is only ever a label. Paging is driven by has_more, never by arithmetic on
 * this number.
 */
function summary(count: number, firstIndex: number, estimatedTotal: number | null | undefined): string {
  if (count === 0) return "No events";

  const range = `${firstIndex.toLocaleString()}–${(firstIndex + count - 1).toLocaleString()}`;
  if (estimatedTotal === null || estimatedTotal === undefined) return `Showing ${range}`;
  return `Showing ${range} of ${estimatedTotal.toLocaleString()} events`;
}

/**
 * Time of day with milliseconds, for the drawer's timeline.
 *
 * The log's own column reads as a date and time now (logTime.ts). Between two
 * events of one message the day never changes and the milliseconds are the
 * point, so the timeline keeps this.
 */
function timeOfDay(at: Date): string {
  const pad = (n: number, width = 2) => String(n).padStart(width, "0");
  return `${pad(at.getHours())}:${pad(at.getMinutes())}:${pad(at.getSeconds())}.${pad(at.getMilliseconds(), 3)}`;
}

export { timeOfDay };
